/**
 * `#intelSelect` — design §6.1 / Amendment 1 §1: "a `<select>` 'Choose analysis' listing the
 * use cases from `GET intelligence/usecases` (order, enabled)" — under the Use Cases tab,
 * one row of the tab's table.
 *
 * Amendment 1 drops the dropdown's old last option, "Analysis tools": the three tabs now do
 * what that option did, so `#intelSelect` lists published use cases only. `useCase === null`
 * (a cold Use Cases tab, or the one a `resetIntel`/tab-close leaves behind) shows a disabled
 * placeholder rather than silently falling onto whichever use case happens to sort first.
 */
import { useTranslation } from 'react-i18next';

import { useAppStore } from '../../lib/state';
import { useUseCases } from './api';

/** The dropdown's own placeholder value — never a real use-case id, never selectable. */
const PLACEHOLDER = '';

export function UseCaseSelect() {
  const { t } = useTranslation();
  const useCase = useAppStore((s) => s.intel.useCase);
  const setIntelUseCase = useAppStore((s) => s.setIntelUseCase);
  const resetIntel = useAppStore((s) => s.resetIntel);
  const { data } = useUseCases();

  return (
    <div className="intel-select-row">
    <select
      id="intelSelect"
      aria-label={t('intel.selectLabel')}
      value={useCase ?? PLACEHOLDER}
      onChange={(e) => setIntelUseCase(e.target.value || null)}
    >
      <option value={PLACEHOLDER} disabled>
        {t('intel.selectPlaceholder')}
      </option>
      {(data ?? [])
        .filter((uc) => uc.enabled)
        .map((uc) => (
          <option key={uc.id} value={uc.id}>
            {uc.title}
          </option>
        ))}
      {/*
        Client §6: a last, disabled entry signalling the roadmap. It opens nothing — the
        registry and the `uc_water` draft already prove the machinery, this is only the
        menu affordance — so it is `disabled`, which also means `onChange` can never see
        its value and the placeholder logic above is untouched.
      */}
      <option value="__create" disabled title={t('intel.createUseCase.tooltip')}>
        {t('intel.createUseCase.label')}
      </option>
    </select>
    {/* NOTES #729: Reset sits beside the dropdown; it restarts the chosen use case. */}
    {useCase ? (
      <button type="button" className="btn ghost sm" id="intelReset" onClick={() => resetIntel()}>
        {t('intel.resetBtn')}
      </button>
    ) : null}
    </div>
  );
}

export default UseCaseSelect;
